import { formatValue } from "../../utils/helpers";
import metricMetadataIcon from '../../assets/metric-metadata-icon-slate.svg';
import { CSSProperties, useRef, useState } from "react";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata } from "../../utils/dashboardUtils";
import DefaultIcon from '../../assets/general/default-metric.svg?react';
import MetricMetadataModal from "../MetricMetadataModal";
import { useCountyFips } from "../../context/CountyFipsContext";
import { useDashboardColor } from "../../context/DashboardColorContext";

interface DoubleMetricCardProps {
    metricOne: DisaggregatedMetric;
    metricTwo: DisaggregatedMetric;
    subtextOne: string;
    subtextTwo: string;
    title: string;
    metadataId: number;
    width: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: React.FC<React.SVGProps<SVGSVGElement>>;
    showMetadataIcon?: boolean;
    showBothMetadataIcons?: boolean;
};

const DoubleMetricCard = ({ metricOne, metricTwo, subtextOne, subtextTwo, title, metadataId, disaggData, geoDetails, geoTag, selectionActive, svg: SvgIcon, showMetadataIcon = true, showBothMetadataIcons = false }: DoubleMetricCardProps) => {
    const { countyFips } = useCountyFips();
    const color = useDashboardColor();  

    const [showMetadata, setShowMetadata] = useState(false);
    const [activeMetadataId, setActiveMetadataId] = useState<number>(metadataId);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Destructure metric properties
    // Destructure metricOne properties
    const { versions: versions1 } = metricOne;
    const { value: value1, unit: unit1, percent_calc: percentCalc1 } = versions1[0];

    // Calculate formatted value for metricOne
    const formattedValue1 = unit1 === "ratio" ? `${percentCalc1?.n}/${percentCalc1?.d}` : value1;

    // Destructure metricTwo properties
    const { versions: versions2 } = metricTwo;
    const { value: value2, unit: unit2, percent_calc: percentCalc2 } = versions2[0];

    // Calculate formatted value for metricTwo
    const formattedValue2 = unit2 === "ratio" ? `${percentCalc2?.n}/${percentCalc2?.d}` : value2;

    const parentMetric = disaggData?.find(m => m.id === metadataId);
    const unitDescriptor = parentMetric ? parentMetric.versions[0]?.unit_descriptor : '';

    const IconComponent = SvgIcon || DefaultIcon;

    // Refs for metadata modal
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const modalId = `metric-metadata-modal-${activeMetadataId ?? 'main'}`;
    const clickDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const countyFipsKey = countyFips ? countyFips.value : 'no-countyfipskey';

    const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});

    function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
        if (fetchCache.current[key]) {
            // Return ongoing request if already in-flight
            return fetchCache.current[key] as Promise<T>;
        }

        const promise = fetchFn().finally(() => {
            delete fetchCache.current[key];
        });

        fetchCache.current[key] = promise;
        return promise;
    }

    const handleClick = (id: number) => {
        if (clickDebounceRef.current) clearTimeout(clickDebounceRef.current);

        clickDebounceRef.current = setTimeout(async () => {
            setActiveMetadataId(id);
            setShowMetadata(true);
            setLoading(true);

            try {
                const distKey = `distData-${id}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`;
                const countyKey = `countyDist-${id}-${countyFipsKey}`;

                const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                    fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id)),
                    fetchWithCache(distKey, () => fetchIndicatorBinCount(id, geoDetails, geoTag)),
                    fetchWithCache(countyKey, () => fetchDistributionData(id, [], countyFips)),
                ]);

                setMetadata(metadataResponse?.data || null);
                setDistributionData(distributionDataResponse || null);
                setCountyDistributionData(countyDistributionDataResponse || null);
            } catch (error) {
                const errorMessage = (error as Error).message || 'An unexpected error occurred.';
                console.error('Error fetching data:', error);
                setError(new Error(`Failed to load data: ${errorMessage}`));
            } finally {
                setLoading(false);
            }
        }, 150);
    };

    const handleCloseMetadata = () => {
        setShowMetadata(false);
    };

    const isCardClickable = !(showMetadataIcon && !showBothMetadataIcons);

    return (
        <div 
            className="relative overflow-hidden group hover:shadow-lg transition-all border rounded-xl bg-white"
            onClick={isCardClickable && !showMetadata ? () => handleClick(metadataId) : undefined}
            role={isCardClickable ? "button" : undefined}
            tabIndex={isCardClickable ? 0 : undefined}
            onKeyDown={
            isCardClickable && !showMetadata
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClick(metadataId);
                    }
                }
                : undefined
            }
        >
            <div className="p-4">
                <div className="flex items-start justify-between space-x-4">
                    {/* Metric Icon (dynamic colors) */}
                    <div
                        className="group rounded-lg p-2 transition-colors bg-[var(--bg)] group-hover:bg-[var(--bg-hover)]"
                        style={
                            {
                            "--bg": `${color}20`,
                            "--bg-hover": `${color}40`,
                            } as CSSProperties
                        }
                    >
                        {SvgIcon && (
                            <IconComponent
                                className="w-8 h-8"
                                style={{ color }}
                                aria-label={`${name} icon`}
                                role="img"
                            />
                        )}
                    </div>

                    {/* Subtext and Values */}
                    <div className="flex flex-col items-start ml-2">
                        <p className="text-xs font-bold" style={{ color: color }}>{subtextOne}</p>

                        <div className="flex items-end">
                            <p className="text-3xl font-semibold tracking-tight -mt-1">{formatValue(formattedValue1, unit1, metricOne.id)}</p>

                            {showMetadataIcon && showBothMetadataIcons && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClick(metricOne.id);
                                    }}
                                    aria-haspopup="dialog"
                                    aria-expanded={showMetadata ? 'true' : 'false'}
                                    aria-controls={modalId}
                                    aria-label={`Additional information about ${subtextOne}`}
                                    className="
                                        mt-1 inline-flex h-6 w-6 items-center justify-center rounded
                                        transition-transform hover:scale-105 focus:scale-105
                                        focus-visible:outline-none
                                        focus-visible:ring-2 focus-visible:ring-offset-2
                                        focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                                        dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
                                    "
                                >
                                    <img
                                        src={metricMetadataIcon}
                                        alt=""
                                        aria-hidden="true"
                                        className="h-3 w-3"
                                        draggable="false"
                                    />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col items-start ml-4">
                        <p className="text-xs font-bold" style={{ color: color }}>{subtextTwo}</p>
                        <div className="flex items-end">
                            <p className="text-3xl font-semibold tracking-tight -mt-1">{formatValue(formattedValue2, unit2, metricTwo.id)}</p>

                            {showMetadataIcon && showBothMetadataIcons && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClick(metricTwo.id);
                                    }}
                                    aria-haspopup="dialog"
                                    aria-expanded={showMetadata ? 'true' : 'false'}
                                    aria-controls={modalId}
                                    aria-label={`Additional information about ${subtextTwo}`}
                                    className="
                                        mt-1 inline-flex h-6 w-6 items-center justify-center rounded
                                        transition-transform hover:scale-105 focus:scale-105
                                        focus-visible:outline-none
                                        focus-visible:ring-2 focus-visible:ring-offset-2
                                        focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                                        dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
                                    "
                                >
                                    <img
                                        src={metricMetadataIcon}
                                        alt=""
                                        aria-hidden="true"
                                        className="h-3 w-3"
                                        draggable="false"
                                    />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Single Metadata Icon (fallback / legacy behavior) */}
                    {showMetadataIcon && !showBothMetadataIcons && ( 
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClick(metadataId);
                            }}
                            aria-haspopup="dialog"
                            aria-expanded={showMetadata ? 'true' : 'false'}
                            aria-controls={modalId}
                            aria-label={`Additional information about ${title}`}
                            className="
                            inline-flex h-6 w-6 items-center justify-center rounded
                            transition-transform hover:scale-105 focus:scale-105
                            focus-visible:outline-none
                            focus-visible:ring-2 focus-visible:ring-offset-2
                            focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                            dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
                            "
                        >
                            <img src={metricMetadataIcon} alt="" aria-hidden="true" className="h-3 w-3" draggable="false" />
                        </button>
                    )}
                </div>

                <div className="space-y-0.5">
                    <p className="text-port-text text-sm font-semibold">{title}</p>

                    {unitDescriptor && <p className={`text-port-text text-xs -mt-1`}>{unitDescriptor}</p>}
                </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />

            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleCloseMetadata}
                id={metadataId}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                geoTag={geoTag}
                // svg={SvgIcon}
                loading={loading}
                error={error}
                triggerRef={triggerRef}
                modalId={modalId}
            />
        </div>
    );
};


export default DoubleMetricCard;
